import { Box, Typography, keyframes } from '@mui/material';
import { UploadCloud } from 'lucide-react';

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(0.95);
  }
`;

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

interface IngestStatusIndicatorProps {
  message?: string;
}

const IngestStatusIndicator = ({ message = 'Processing files...' }: IngestStatusIndicatorProps) => {
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 80,
        right: 24,
        zIndex: 1200,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        bgcolor: 'background.paper',
        borderRadius: 2,
        px: 2,
        py: 1.5,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        border: '1px solid',
        borderColor: 'primary.light',
        animation: `${pulse} 2s ease-in-out infinite`,
      }}
    >
      {/* Spinning icon container */}
      <Box
        sx={{
          position: 'relative',
          width: 32,
          height: 32,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Outer spinning ring */}
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            border: '2px solid transparent',
            borderTopColor: 'primary.main',
            borderRightColor: 'primary.light',
            animation: `${spin} 1s linear infinite`,
          }}
        />
        {/* Inner icon */}
        <Box
          sx={{
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <UploadCloud size={16} />
        </Box>
      </Box>

      {/* Text content */}
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            lineHeight: 1.2,
          }}
        >
          {message}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            lineHeight: 1.2,
          }}
        >
          This may take a moment
        </Typography>
      </Box>
    </Box>
  );
};

export default IngestStatusIndicator;
